import { useState, useEffect } from 'react';
import { Bell, BellOff, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface NotificationPreferencesProps {
  accessToken: string;
  currentUser?: any;
  onClose?: () => void;
}

interface Preferences {
  muteAllExceptMentions: boolean;
  emailNotifications: boolean;
  inAppNotifications: boolean;
}

export function NotificationPreferences({ accessToken, currentUser, onClose }: NotificationPreferencesProps) {
  const [preferences, setPreferences] = useState<Preferences>({
    muteAllExceptMentions: false,
    emailNotifications: true,
    inAppNotifications: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/mentions/preferences`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toast.error('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (newPrefs: Preferences) => {
    try {
      setSaving(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/mentions/preferences`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newPrefs),
        }
      );

      if (response.ok) {
        toast.success('Notification preferences updated');
      } else {
        throw new Error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key: keyof Preferences) => {
    const newPrefs = {
      ...preferences,
      [key]: !preferences[key],
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mute all except mentions */}
        <div className="flex items-start justify-between space-x-4">
          <div className="space-y-1 flex-1">
            <Label className="text-base flex items-center gap-2">
              {preferences.muteAllExceptMentions ? (
                <BellOff className="size-4 text-orange-600" />
              ) : (
                <Bell className="size-4 text-blue-600" />
              )}
              Mute all except @mentions
            </Label>
            <p className="text-sm text-muted-foreground">
              Only receive notifications when someone specifically mentions you with @
            </p>
          </div>
          <Button
            variant={preferences.muteAllExceptMentions ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleToggle('muteAllExceptMentions')}
            disabled={saving}
            className={preferences.muteAllExceptMentions ? 'bg-orange-600 hover:bg-orange-700' : ''}
          >
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : preferences.muteAllExceptMentions ? (
              <>
                <Check className="size-4 mr-2" />
                Enabled
              </>
            ) : (
              'Enable'
            )}
          </Button>
        </div>

        <div className="h-px bg-border" />

        {/* In-app notifications */}
        <div className="flex items-start justify-between space-x-4">
          <div className="space-y-1 flex-1">
            <Label className="text-base">In-App Notifications</Label>
            <p className="text-sm text-muted-foreground">
              Show notifications in the application
            </p>
          </div>
          <Button
            variant={preferences.inAppNotifications ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleToggle('inAppNotifications')}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : preferences.inAppNotifications ? (
              <>
                <Check className="size-4 mr-2" />
                Enabled
              </>
            ) : (
              'Enable'
            )}
          </Button>
        </div>

        <div className="h-px bg-border" />

        {/* Email notifications */}
        <div className="flex items-start justify-between space-x-4">
          <div className="space-y-1 flex-1">
            <Label className="text-base">Email Notifications</Label>
            <p className="text-sm text-muted-foreground">
              Receive notifications via email
            </p>
          </div>
          <Button
            variant={preferences.emailNotifications ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleToggle('emailNotifications')}
            disabled={saving}
          >
            {saving ? (
              <Loader2 className="size-4 animate-spin" />
            ) : preferences.emailNotifications ? (
              <>
                <Check className="size-4 mr-2" />
                Enabled
              </>
            ) : (
              'Enable'
            )}
          </Button>
        </div>
    </div>
  );
}
